/**
 * The full record: everything recorded for this patient, form by form, newest first, with every
 * value outside its threshold marked. It prints as a plain document: the copy she carries, and the
 * copy a receiving facility reads.
 */
import {
  type ConceptDef,
  type Encounter,
  type Observation,
  type Patient,
  type PatientRecord,
  SYS_PROGRAMME,
  conceptOf,
  evalCondition,
  formOfEncounter,
  observationValue,
  splitRecord,
  urgencyOf,
} from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { RemovedBanner } from '../components/Removal.js';
import { ecClock } from '../components/SeriesCard.js';
import { app } from '../controller.js';
import { clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { allPacks } from '../packs.js';
import { ageOf, birthLabel, displayName, mrnOf } from '../patient.js';
import { PatientChrome } from '../shell.js';

const concepts = new Map<string, ConceptDef>();
const forms = new Map<string, string>();
for (const p of allPacks) {
  for (const c of p.concepts) if (!concepts.has(c.code)) concepts.set(c.code, c);
  for (const f of p.forms) forms.set(f.id, f.title);
}

/** A recorded value as words: the choice's label, yes/no, a unit, a clinic time. */
export function showValue(c: ConceptDef | undefined, v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? t.yes : t.no;
  if (c?.type === 'datetime' && typeof v === 'string') return `${eth(v)} ${ecClock(v)}`;
  if (c?.type === 'date' && typeof v === 'string') return eth(v);
  const ch = c?.choices?.find((x) => String(x.code) === String(v));
  if (ch) return ch.display;
  return `${v}${c?.unit ? ` ${c.unit}` : ''}`;
}

export const isAbnormal = (c: ConceptDef | undefined, v: unknown) =>
  !!c?.abnormal &&
  v !== null &&
  v !== undefined &&
  evalCondition(c.abnormal, { value: v as never });

export function FullRecord({ id }: { id: string }) {
  const a = useApp();
  const [p, setP] = useState<Patient | null>(null);
  const [rec, setRec] = useState<PatientRecord | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when the store changes
  useEffect(() => {
    void app.session?.get<Patient>('Patient', id).then(setP);
    void app.session?.forPatient(id).then((rs) => setRec(splitRecord(rs)));
  }, [id, a.status]);
  if (!p || !rec) return null;
  const byEnc = new Map<string, Observation[]>();
  for (const o of rec.observations) {
    const k = o.encounter?.reference ?? '';
    byEnc.set(k, [...(byEnc.get(k) ?? []), o]);
  }
  const encs = [...rec.encounters].sort((x, y) => y.period.start.localeCompare(x.period.start));
  const age = ageOf(p, clock.today());
  const head: Array<[string, string | undefined]> = [
    [t.mrn, mrnOf(p)],
    [
      t.sex,
      p.gender === 'female'
        ? t.female
        : p.gender === 'male'
          ? t.male
          : p.gender === 'other'
            ? t.otherSex
            : t.sexNotRecorded,
    ],
    [t.ageLabel, age ? t.age(age) : undefined],
    [t.birthDate, birthLabel(p) ?? undefined],
    [t.phone, p.telecom?.find((x) => x.system === 'phone')?.value],
    [t.kebele, p.address?.[0]?.line?.[0]],
    [t.woreda, p.address?.[0]?.district],
  ];
  return (
    <section>
      <PatientChrome id={id} here="record" />
      <div class="card rec" data-card="full-record">
        <div class="dhead">
          <div>
            <h3>
              {t.fullRecord}: {displayName(p)}
            </h3>
            <p class="muted">{t.printedOn(eth(clock.today()))}</p>
          </div>
          <button type="button" class="sec" onClick={() => window.print()}>
            {t.print}
          </button>
        </div>
        <RemovedBanner record={p} />
        <div class="rec-b">
          <div class="rec-g">
            {head
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div class="rec-f" key={k}>
                  <span class="rec-l">{k}</span>
                  <span class="rec-v">{v}</span>
                </div>
              ))}
          </div>
        </div>
        <div class="rec-b">
          <h5>{t.programmes}</h5>
          {rec.episodes.length ? (
            <ul class="plain">
              {rec.episodes.map((e) => {
                const c = e.type
                  .flatMap((x) => x.coding ?? [])
                  .find((x) => x.system === SYS_PROGRAMME);
                return (
                  <li key={e.id}>
                    <strong>{c?.display ?? c?.code}</strong>{' '}
                    <span class="muted">
                      {e.period?.start ? eth(e.period.start) : ''}
                      {e.period?.end ? ` – ${eth(e.period.end)}` : ''} · {e.status}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p class="muted">{t.noProgrammes}</p>
          )}
        </div>
        {encs.map((e) => (
          <EncounterBlock key={e.id} e={e} obs={byEnc.get(`Encounter/${e.id}`) ?? []} />
        ))}
        {rec.referrals.length ? (
          <div class="rec-b">
            <h5>{t.referrals}</h5>
            <ul class="plain">
              {rec.referrals.map((r) => (
                <li key={r.id}>
                  {eth(r.authoredOn)} · {r.reasonCode?.[0]?.text ?? r.code?.text}
                  {urgencyOf(r) !== 'routine' ? ` · ${t.urgencies[urgencyOf(r)]}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function EncounterBlock({ e, obs }: { e: Encounter; obs: Observation[] }) {
  const form = formOfEncounter(e);
  const title =
    (form && forms.get(form)) ??
    e.type
      ?.map((x) => x.coding?.[0]?.display)
      .filter(Boolean)
      .join(', ') ??
    t.visits;
  return (
    <div class="rec-b" data-encounter={e.id}>
      <h5>
        {title} · {eth(e.period.start)} {ecClock(e.period.start)}
      </h5>
      {obs.length ? (
        <div class="rec-g">
          {obs.map((o) => {
            const code = conceptOf(o) ?? '';
            const c = concepts.get(code);
            const v = observationValue(o);
            return (
              <div class="rec-f" key={o.id}>
                <span class="rec-l">{c?.display ?? code}</span>
                <span class={isAbnormal(c, v) ? 'rec-v rec-alarm' : 'rec-v'}>
                  {showValue(c, v)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p class="muted">{t.nothingRecorded}</p>
      )}
    </div>
  );
}
